import React, { createContext, useContext, useState, useEffect, useCallback, useRef } from 'react';
import { coachingAPI, setViewingAthleteId, subscribePendingRequests, getPendingRequestCount } from '../services/api';
import { useAuth } from './AuthContext';
import { useNotifications } from './NotificationSSEContext';
import { storedUserId } from '../lib/userStorage';

const CoachingContext = createContext();

// Persist last-known relationships so the sidebar dropdown and any
// athlete-selector UI render with their previous values during the
// brief window between page load and the first /coaching/relationships
// response. Without this the dropdown unmounts on every refresh
// (athletes.length === 0) and reappears 3+ seconds later, which feels
// like a regression even though the data is app-level.
const RELATIONSHIPS_STORAGE_KEY = 'aithlo.coachingRelationships';

// The cache carries the id of the user it belongs to and is used only for that
// user: the next account on this browser never sees someone else's roster
// (aithlo-frontend#46).
const loadCachedRelationships = (userId) => {
  try {
    if (!userId) return null;
    const raw = localStorage.getItem(RELATIONSHIPS_STORAGE_KEY);
    if (!raw) return null;
    const parsed = JSON.parse(raw);
    if (!parsed || parsed.userId !== userId) return null;
    return {
      athletes: Array.isArray(parsed.athletes) ? parsed.athletes : [],
      coaches: Array.isArray(parsed.coaches) ? parsed.coaches : [],
      pendingReceived: Array.isArray(parsed.pendingReceived) ? parsed.pendingReceived : [],
      pendingSent: Array.isArray(parsed.pendingSent) ? parsed.pendingSent : [],
    };
  } catch {
    return null;
  }
};

// Restore athlete selection synchronously so it's available on the very first render
const restoreViewingAthlete = () => {
  const saved = sessionStorage.getItem('viewingAthlete');
  if (saved) {
    try {
      const parsed = JSON.parse(saved);
      setViewingAthleteId(parsed.user_id);
      return parsed;
    } catch {
      sessionStorage.removeItem('viewingAthlete');
    }
  }
  return null;
};

export const CoachingProvider = ({ children }) => {
  const { user } = useAuth();
  const selfId = user?.id ?? user?.user_id ?? null;
  const [_cachedRelationships] = useState(() => loadCachedRelationships(selfId ?? storedUserId()));
  const [viewingAthlete, setViewingAthlete] = useState(restoreViewingAthlete);
  const [athletes, setAthletes] = useState(_cachedRelationships?.athletes || []);
  const [coaches, setCoaches] = useState(_cachedRelationships?.coaches || []);
  const [pendingReceived, setPendingReceived] = useState(_cachedRelationships?.pendingReceived || []);
  const [pendingSent, setPendingSent] = useState(_cachedRelationships?.pendingSent || []);
  const [unreadCount, setUnreadCount] = useState(0);
  const [isSwitchingAthlete, setIsSwitchingAthlete] = useState(false);
  // The hub's count, while a coach views an athlete only; everyone's own badge
  // is V2Context's useCoachingBadge (coaching_unread, aithlo-frontend#67).
  const { coachingCount: sseCoachingCount, hub = false } = useNotifications() || {};

  // Persist relationships every time they change — the cache stays in
  // sync with the most recent fetch, so a refresh always hydrates with
  // up-to-date data. Real fetch still fires on mount and overwrites it.
  useEffect(() => {
    if (!selfId) return;
    try {
      localStorage.setItem(
        RELATIONSHIPS_STORAGE_KEY,
        JSON.stringify({ userId: selfId, athletes, coaches, pendingReceived, pendingSent }),
      );
    } catch {
      // localStorage may be unavailable (private mode); ignore.
    }
  }, [selfId, athletes, coaches, pendingReceived, pendingSent]);

  // Begin a "switching" window: listen for in-flight requests triggered by
  // the switch and clear the flag once all of them settle. Hard cap at 8s.
  const beginSwitchLoading = useCallback(() => {
    setIsSwitchingAthlete(true);
    let sawActivity = false;
    const unsubscribe = subscribePendingRequests((count) => {
      if (count > 0) sawActivity = true;
      if (sawActivity && count === 0) {
        cleanup();
      }
    });
    const cleanup = () => {
      unsubscribe();
      clearTimeout(timeoutId);
      setIsSwitchingAthlete(false);
    };
    const timeoutId = setTimeout(cleanup, 8000);
    // If no requests fire within a short window, clear anyway.
    setTimeout(() => {
      if (!sawActivity && getPendingRequestCount() === 0) cleanup();
    }, 1500);
  }, []);

  const fetchRelationships = useCallback(async () => {
    if (!user) return;
    try {
      const data = await coachingAPI.getRelationships();
      setAthletes(data.as_coach || []);
      setCoaches(data.as_athlete || []);
      setPendingReceived(data.pending_received || []);
      setPendingSent(data.pending_sent || []);
    } catch (err) {
      console.error('Failed to fetch coaching relationships:', err);
    }
  }, [user]);

  const fetchUnreadCount = useCallback(async () => {
    if (!user || !hub) return;   // not viewing: the stream's coaching.unread brings the new count
    try {
      const data = await coachingAPI.getUnreadCount();
      setUnreadCount(data.count || 0);
    } catch {
      // silent
    }
  }, [user, hub]);

  // Sync unread count from the hub (while viewing only)
  useEffect(() => {
    if (hub) setUnreadCount(sseCoachingCount);
  }, [sseCoachingCount, hub]);

  // Fetch relationships immediately when a user is present. The 3-second
  // deferral that used to live here was added before the DB pool when
  // each request paid ~260ms of connect overhead — pushing this fetch
  // to the back of the queue meant page-critical data went first.
  // With pooling, the relationships fetch is sub-100ms and runs fine in
  // parallel with the calendar's primary load.
  const hadUserRef = useRef(false);
  useEffect(() => {
    if (user) {
      hadUserRef.current = true;
      fetchRelationships();
    } else if (hadUserRef.current) {
      // Only clear on actual logout (user was set, now null), not on initial mount
      hadUserRef.current = false;
      setAthletes([]);
      setCoaches([]);
      setPendingReceived([]);
      setPendingSent([]);
      setUnreadCount(0);
      try {
        // Wipe persisted data so the next user starts fresh.
        localStorage.removeItem(RELATIONSHIPS_STORAGE_KEY);
      } catch {
        // ignore
      }
      switchToSelf();
    }
  }, [user, fetchRelationships]);

  const switchToAthlete = useCallback((athlete) => {
    setViewingAthlete(athlete);
    setViewingAthleteId(athlete.user_id);
    sessionStorage.setItem('viewingAthlete', JSON.stringify(athlete));
    beginSwitchLoading();
  }, [beginSwitchLoading]);

  const switchToSelf = useCallback(() => {
    setViewingAthlete(null);
    setViewingAthleteId(null);
    sessionStorage.removeItem('viewingAthlete');
    beginSwitchLoading();
  }, [beginSwitchLoading]);

  const isViewingAthlete = !!viewingAthlete;

  const coachUnreadTotal = coaches.reduce((sum, c) => sum + (c.unread_count || 0), 0);

  return (
    <CoachingContext.Provider
      value={{
        viewingAthlete,
        isViewingAthlete,
        athletes,
        coaches,
        pendingReceived,
        pendingSent,
        unreadCount,
        coachUnreadTotal,
        isSwitchingAthlete,
        switchToAthlete,
        switchToSelf,
        fetchRelationships,
        fetchUnreadCount,
      }}
    >
      {children}
    </CoachingContext.Provider>
  );
};

export const useCoaching = () => useContext(CoachingContext);
